/**
 * Webpack Dev Server 错误拦截工具
 * 用于处理和抑制开发环境中的特定错误
 */

/**
 * 检查是否是需要抑制的错误
 * @param {Error|String} error 错误对象或错误消息
 * @returns {Boolean} 是否是需要抑制的错误
 */
export function isSuppressedError(error) {
  if (!error) return false;
  
  const message = typeof error === 'string' ? error : (error.message || '');
  const stack = typeof error === 'object' ? (error.stack || '') : '';
  
  // 常见的需要抑制的错误模式
  const suppressedErrorPatterns = [
    'ResizeObserver loop completed with undelivered notifications',
    'ResizeObserver loop limit exceeded',
    'ResizeObserver',
    'undelivered notifications',
    'Cannot read properties of null',
    'insertBefore',
    'appendChild',
    'removeChild',
    'replaceChild',
    'firstChild',
    'nextSibling',
    'parentNode',
    'webpack-dev-server',
    'overlay.js',
    'handleError',
    'webpack-internal',
    'client/overlay.js'
  ];
  
  // 检查是否包含需要抑制的错误关键字
  const messageMatch = suppressedErrorPatterns.some(pattern => 
    message.toLowerCase().includes(pattern.toLowerCase())
  );
  
  const stackMatch = suppressedErrorPatterns.some(pattern => 
    stack.toLowerCase().includes(pattern.toLowerCase())
  );
  
  return messageMatch || stackMatch;
}

/**
 * 设置全局错误处理拦截器
 * @returns {Function} 清理函数
 */
export function setupWebpackErrorInterceptor() {
  // 保存原始的错误处理函数
  const originalErrorHandler = window.onerror;
  const originalUnhandledRejection = window.onunhandledrejection;
  const originalConsoleError = console.error;
  
  // 专门处理ResizeObserver错误的全局拦截器
  const handleResizeObserverError = (error) => {
    const errorMessage = typeof error === 'string' ? error : (error?.message || '');
    if (errorMessage.includes('ResizeObserver') || 
        errorMessage.includes('undelivered notifications') ||
        errorMessage.includes('loop completed')) {
      // 完全静默处理ResizeObserver错误
      return true;
    }
    return false;
  };
  
  // 拦截 console.error 来抑制 ResizeObserver 错误
  console.error = function(...args) {
    const message = args.join(' ');
    if (handleResizeObserverError(message) || isSuppressedError(message)) {
      // 静默处理，不输出到控制台
      return;
    }
    originalConsoleError.apply(console, args);
  };
  
  // 处理 JavaScript 运行时错误
  window.onerror = function(message, source, lineno, colno, error) {
    if (handleResizeObserverError(error || message) || isSuppressedError(error || message)) {
      // 完全静默处理，不输出任何信息
      return true; // 阻止默认错误处理
    }
    
    // 调用原始错误处理函数
    if (originalErrorHandler) {
      return originalErrorHandler.call(this, message, source, lineno, colno, error);
    }
    
    return false;
  };
  
  // 处理未捕获的 Promise 拒绝
  window.addEventListener('unhandledrejection', function(event) {
    if (handleResizeObserverError(event.reason) || isSuppressedError(event.reason)) {
      // 完全静默处理
      event.preventDefault();
      return true;
    }
  });
  
  // 处理全局错误事件
  const handleErrorEvent = function(event) {
    if (handleResizeObserverError(event.error || event.message) || isSuppressedError(event.error || event.message)) {
      // 完全静默处理
      event.preventDefault();
      event.stopImmediatePropagation();
      return true;
    }
    return false;
  };
  
  window.addEventListener('error', handleErrorEvent, true);
  
  // 拦截 webpack-dev-server 的错误覆盖层
  const originalCreateElement = document.createElement;
  document.createElement = function(tagName) {
    const element = originalCreateElement.call(this, tagName);
    if (tagName.toLowerCase() === 'div' || tagName.toLowerCase() === 'iframe') {
      // 监听元素属性变化，防止错误覆盖层显示
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type === 'attributes') {
            const target = mutation.target;
            if (target.style && target.style.position === 'fixed' && 
                target.style.zIndex && parseInt(target.style.zIndex) > 1000) {
              // 检查是否是错误覆盖层
              const content = target.textContent || target.innerHTML || '';
              if (handleResizeObserverError(content) || isSuppressedError(content)) {
                target.style.display = 'none';
                target.remove();
              }
            }
          }
        });
      });
      observer.observe(element, { attributes: true, subtree: true });
    }
    return element;
  };
  
  // 返回清理函数
  return function cleanup() {
    window.onerror = originalErrorHandler;
    window.onunhandledrejection = originalUnhandledRejection;
    console.error = originalConsoleError;
    document.createElement = originalCreateElement;
    window.removeEventListener('error', handleErrorEvent, true);
  };
}

/**
 * 优化 ResizeObserver 性能
 */
export function optimizeResizeObserver() {
  if (typeof ResizeObserver !== 'undefined') {
    const originalResizeObserver = window.ResizeObserver;
    
    window.ResizeObserver = class extends originalResizeObserver {
      constructor(callback) {
        const wrappedCallback = (entries) => {
          try {
            // 使用 requestAnimationFrame 和防抖优化性能
            requestAnimationFrame(() => {
              try {
                callback(entries);
              } catch (error) {
                // 静默处理 ResizeObserver 回调中的错误
                if (isSuppressedError(error)) {
                  return;
                }
                // 只有非抑制错误才重新抛出
                throw error;
              }
            });
          } catch (error) {
            // 静默处理所有 ResizeObserver 相关错误
            if (isSuppressedError(error)) {
              return;
            }
            console.error('ResizeObserver error:', error);
          }
        };
        
        super(wrappedCallback);
      }
      
      // 重写 observe 方法，添加错误处理
      observe(target, options) {
        try {
          super.observe(target, options);
        } catch (error) {
          if (isSuppressedError(error)) {
            return;
          }
          throw error;
        }
      }
      
      // 重写 unobserve 方法，添加错误处理
      unobserve(target) {
        try {
          super.unobserve(target);
        } catch (error) {
          if (isSuppressedError(error)) {
            return;
          }
          throw error;
        }
      }
      
      // 重写 disconnect 方法，添加错误处理
      disconnect() {
        try {
          super.disconnect();
        } catch (error) {
          if (isSuppressedError(error)) {
            return;
          }
          throw error;
        }
      }
    };
    
    // 完全禁用 ResizeObserver 错误报告
    const originalReportError = window.reportError;
    if (originalReportError) {
      window.reportError = function(error) {
        if (isSuppressedError(error)) {
          return;
        }
        originalReportError.call(this, error);
      };
    }
  }
}

export default {
  isSuppressedError,
  setupWebpackErrorInterceptor,
  optimizeResizeObserver
};